import { useEffect, useRef } from 'react';

const TAG = { bg: '#2a2a2a', color: '#fff' };

const STEPS = [
  {
    num: '01',
    label: 'Aurelix Growth',
    title: 'Build. Acquire. Convert. Scale.',
    desc: 'Everything related to digital growth and revenue.',
    tags: [
      'Shopify Development',
      'Custom Website Development',
      'Web Design & Development',
      'CRO',
      'Landing Pages',
      'Paid Media',
      'Performance Marketing',
      'SEO',
      'Quick Commerce Growth',
      'LinkedIn Campaigns',
    ],
  },
  {
    num: '02',
    label: 'Aurelix Intelligence',
    title: 'Turn data into direction.',
    desc: 'Your business is already generating data. We help you make it useful.',
    tags: [
      'Marketing Analytics',
      'Data Analytics',
      'Business Intelligence',
      'Custom Dashboards',
      'Performance Reporting',
      'Attribution & Funnel Analysis',
      'Customer & Marketing Insights',
      'Data Infrastructure',
    ],
  },
  {
    num: '03',
    label: 'Aurelix Commerce',
    title: 'Where brands meet customers.',
    desc: 'We help brands build and grow across the platforms where customers discover, shop and come back to buy.',
    tags: [
      'Amazon Growth',
      'Flipkart Management',
      'Meesho Management',
      'Quick Commerce',
      'Marketplace Strategy',
      'Product Listings & Optimisation',
      'Amazon A+ Content',
      'Marketplace Advertising',
      'Catalogue Management',
      'Product Launches',
      'WhatsApp Automation',
      'WhatsApp Marketing',
      'Abandoned Cart Automation',
      'Customer Retention',
      'Lifecycle Automation',
    ],
  },
  {
    num: '04',
    label: 'Aurelix AI',
    title: 'AI systems designed around how your business actually works.',
    desc: 'Intelligence helps you understand what is happening. AI helps your business do something about it.',
    tags: [
      'AI Automations',
      'AI Agents',
      'Internal AI Tools',
      'Workflow Automation',
      'AI-Powered Customer Support',
      'Knowledge Systems',
      'Custom AI Solutions',
      'AI Integration & Implementation',
      'Agentic Workflows',
    ],
  },
];

const CELL = 35;

export default function Process() {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const gridCanvasRef = useRef(null);

  // ── Scroll animation effect ──
  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const vh = window.innerHeight;
      const isMobile = window.innerWidth <= 600;

      cardRefs.current.forEach((card) => {
        if (!card) return;
        const r = card.getBoundingClientRect();
        const cy = r.top + r.height / 2;
        const rel = (cy - vh * 0.52) / vh;
        const p = Math.max(0, Math.min(1, 1 - rel * 2.6));

        const numEl = card.querySelector('.ps-num');
        const contentEl = card.querySelector('.ps-content');
        const numFill = card.querySelector('.ps-num-fill');

        const numSlide = isMobile ? 40 : 120;
        const contentSlide = isMobile ? 40 : 120;
        const blurNum = isMobile ? 6 : 14;
        const blurContent = isMobile ? 6 : 12;

        if (numEl) {
          numEl.style.transform = `translateX(${(1 - p) * -numSlide}px)`;
          numEl.style.opacity = String(p);
          numEl.style.filter = `blur(${(1 - p) * blurNum}px)`;
        }
        if (numFill) {
          const pct = Math.round(p * 100);
          numFill.style.clipPath = `inset(${100 - pct}% 0 0 0)`;
        }
        if (contentEl) {
          contentEl.style.transform = `translateX(${(1 - p) * contentSlide}px)`;
          contentEl.style.opacity = String(p);
          contentEl.style.filter = `blur(${(1 - p) * blurContent}px)`;
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Grid hover canvas effect ──
  useEffect(() => {
    const section = sectionRef.current;
    const canvas = gridCanvasRef.current;
    if (!section || !canvas) return;

    const ctx = canvas.getContext('2d');
    let cells = [], cols, rows, W, H;
    let lastCol = -1, lastRow = -1;
    let rafId;

    function resize() {
      W = section.offsetWidth;
      H = section.offsetHeight;
      canvas.width = W;
      canvas.height = H;
      cols = Math.ceil(W / CELL) + 1;
      rows = Math.ceil(H / CELL) + 1;
      cells = Array.from({ length: rows * cols }, (_, i) => ({
        col: i % cols,
        row: Math.floor(i / cols),
        alpha: 0,
        target: 0,
        speed: 0.08 + Math.random() * 0.05,
      }));
    }

    function getCell(c, r) {
      if (c < 0 || c >= cols || r < 0 || r >= rows) return null;
      return cells[r * cols + c];
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      for (const c of cells) {
        const diff = c.target - c.alpha;
        c.alpha += diff * c.speed;
        if (c.alpha > 0.002) {
          ctx.fillStyle = `rgba(236,43,40,${c.alpha * 0.55})`;
          ctx.fillRect(c.col * CELL + 0.5, c.row * CELL + 0.5, CELL - 1, CELL - 1);
        }
      }
      rafId = requestAnimationFrame(draw);
    }

    function onMove(e) {
      const rect = section.getBoundingClientRect();
      const col = Math.floor((e.clientX - rect.left) / CELL);
      const row = Math.floor((e.clientY - rect.top) / CELL);
      if (col === lastCol && row === lastRow) return;
      lastCol = col;
      lastRow = row;

      for (const c of cells) c.target = 0;

      const main = getCell(col, row);
      if (main) main.target = 1;

      const neighbors = [
        [col - 1, row], [col + 1, row],
        [col, row - 1], [col, row + 1],
        [col - 1, row - 1], [col + 1, row - 1],
        [col - 1, row + 1], [col + 1, row + 1],
      ];
      for (const [nc, nr] of neighbors) {
        const cell = getCell(nc, nr);
        if (cell) cell.target = 0.22;
      }
    }

    function onLeave() {
      lastCol = -1;
      lastRow = -1;
      for (const c of cells) c.target = 0;
    }

    resize();
    draw();

    section.addEventListener('mousemove', onMove);
    section.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(rafId);
      section.removeEventListener('mousemove', onMove);
      section.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return null;
}